<script lang="ts">
  import * as echarts from "echarts/core";
  import { BarChart } from "echarts/charts";
  import { GridComponent } from "echarts/components";
  import { CanvasRenderer } from "echarts/renderers";
  import type { EChartsType } from "echarts/core";
  import type { WorkoutSummary } from "@contract/types";
  import { aggregateRecentWeeks } from "../lib/aggregates";
  import { fmtMeters } from "../lib/format";

  interface Props {
    workouts: WorkoutSummary[];
  }
  let { workouts }: Props = $props();

  echarts.use([BarChart, GridComponent, CanvasRenderer]);

  let trendChartEl: HTMLDivElement | null = $state(null);
  let trendChart: EChartsType | null = null;

  let recentWeeks = $derived(aggregateRecentWeeks(workouts, 8));
  let currentWeekM = $derived(
    recentWeeks.length > 0
      ? Math.round(recentWeeks[recentWeeks.length - 1].totalDistanceMeters)
      : 0,
  );
  let weeksWithSwims = $derived(
    recentWeeks.filter((w) => w.totalDistanceMeters > 0).length,
  );

  // No "This week: N m" branch — the ring directly above this card already
  // says this week's meters (persona gate 2026-07-10, 4/5: the same number
  // echoed 2-3x before any new fact). The previous week is NEW information,
  // so that line stays.
  let trendCaption = $derived.by((): string => {
    if (weeksWithSwims < 2) return "Your distance shows up here as you swim.";
    for (let i = recentWeeks.length - 2; i >= 0; i--) {
      const m = Math.round(recentWeeks[i].totalDistanceMeters);
      if (m > 0) return `Last week: ${fmtMeters(m)}`;
    }
    return "Your distance shows up here as you swim.";
  });

  $effect(() => {
    if (!trendChartEl) return;
    if (!trendChart) {
      trendChart = echarts.init(trendChartEl, null, { renderer: "canvas" });
    }
    const lastIdx = recentWeeks.length - 1;
    // ONE color system (persona gate final, B2): every week is periwinkle
    // (#A9CAEE); a low/zero week is just SHORT (barMinHeight keeps it visible),
    // never a separate charcoal swatch — the old dist===0 grey read as broken /
    // no-data to 3/5. The current (rightmost) week is the same periwinkle at 0.5
    // opacity ("still going"), matching the smoothness card's faded-bar fade so
    // "faded" reads identically across both charts (B3).
    const data = recentWeeks.map((w, i) => {
      const dist = Math.round(w.totalDistanceMeters);
      return {
        value: dist,
        itemStyle: {
          color: "#A9CAEE",
          opacity: i === lastIdx ? 0.5 : 1,
          borderRadius: [4, 4, 0, 0],
        },
      };
    });

    // (The old dimmed average reference line is gone — persona gate
    // 2026-07-10: an unlabeled horizontal line read as a rendering artifact,
    // and labeling it would need sub-15px text. The bars carry the story.)
    trendChart.setOption({
      grid: { left: 8, right: 8, top: 12, bottom: 12 },
      xAxis: {
        type: "category",
        // 8 bars carry the relative story. x-axis labels dropped per spec
        // (sub-15px date strings violate the font-size floor; exact dates
        // live in the all-swims list directly below).
        data: recentWeeks.map(() => ""),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "value",
        show: false,
        splitLine: { show: false },
      },
      series: [
        {
          type: "bar",
          data,
          barWidth: "52%",
          // barMinHeight gives genuinely-low / zero weeks a clearly-intentional
          // short stub (with the same rounded top as the others) instead of a
          // 2px sliver that read as a clipped 9th bar / render glitch (persona
          // gate round 4, B4, 4/5). Exactly 8 bars render (aggregateRecentWeeks
          // returns 8); a real tall week is ~90px, so an 8px floor still reads
          // as "a low week", not "as tall as a full week".
          barMinHeight: 8,
        },
      ],
    });
  });

  $effect(() => {
    void recentWeeks;
    trendChart?.resize();
  });
</script>

<div class="trend-card">
  <div class="trend-head">
    <div class="trend-label">Last 8 weeks, distance</div>
    <div class="trend-caption">{trendCaption}</div>
  </div>
  <div class="trend-chart" bind:this={trendChartEl}></div>
  {#if currentWeekM > 0}
    <!-- Name the muted rightmost bar (persona gate 2026-07-10, B5): an
         unexplained faded bar read as a rendering glitch, and it also anchors
         the "Last week" caption — the faded bar is this week, so the solid bar
         before it is last week. Mirrors the Glide card's faded-bar note so the
         two charts behave the same. -->
    <p class="trend-note">The faded bar on the right is this week, still going.</p>
  {/if}
</div>

<style>
  .trend-card {
    background: var(--bg-card);
    border: 0;
    border-radius: var(--radius-card);
    padding: var(--space-4);
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
  }
  .trend-label {
    /* Card-title label tier (12px uppercase) per DESIGN.md — quiet by design,
       a clear step below the caption/body text that reads the actual number. */
    font-size: 12px;
    color: var(--text-muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: var(--space-2);
  }
  .trend-caption {
    font-size: 15px;
    color: #C5C6CC;
    font-variant-numeric: tabular-nums;
  }
  .trend-chart {
    height: 120px;
    margin-top: var(--space-3);
  }
  /* Muted-bar note (14px muted). */
  .trend-note {
    font-size: 14px;
    color: var(--text-muted);
    margin: var(--space-2) 0 0;
    line-height: 1.35;
  }
</style>
